import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, Smartphone, Banknote, Tag, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import useSeo from "@/lib/useSeo";
import { useShop } from "@/context/ShopContext";
import { inr } from "@/lib/content";
import UpiPayment from "@/components/site/UpiPayment";

const COD_NOTICE = "Notice: Cash on Delivery (COD) is temporarily disabled due to heavy festive rush and high order volume during the Dandiya & Navratri Season. To ensure guaranteed 2-day priority dispatch for your Kurtaani outfit, we are currently accepting 100% Secure Prepaid Orders only.";
const FIELDS = [["name", "Full name", "sm:col-span-2"], ["phone", "Mobile number (10 digits)", ""], ["email", "Email", ""], ["address", "House no., street, area", "sm:col-span-2"], ["city", "City", ""], ["state", "State", ""], ["pincode", "Pincode", ""]];

const Row = ({ l, v, green, testid }) => (
  <div className={`flex justify-between text-sm ${green ? "text-[#1B4332]" : ""}`}><span>{l}</span><span data-testid={testid}>{v}</span></div>
);

export default function Checkout() {
  useSeo("Secure Checkout", null);
  const { cart, clearCart } = useShop();
  const nav = useNavigate();
  const [form, setForm] = useState(() => JSON.parse(localStorage.getItem("kt_addr") || "null") || { name: "", phone: "", email: "", address: "", city: "", state: "", pincode: localStorage.getItem("kt_pin") || "" });
  const method = "upi";
  const [order, setOrder] = useState(null);
  const [couponInput, setCouponInput] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const items = cart.map(({ slug, size, color, qty }) => ({ slug, size, color, qty }));
  const key = JSON.stringify(items);

  useEffect(() => {
    if (!items.length) return;
    api.post("/cart/quote", { items, payment_method: method, coupon }).then((r) => setQuote(r.data)).catch((e) => { toast.error(errMsg(e)); if (coupon) setCoupon(null); });
  }, [key, method, coupon]); // eslint-disable-line

  const applyCoupon = async () => {
    try {
      await api.post("/coupons/validate", { items, payment_method: method, coupon: couponInput });
      setCoupon(couponInput.trim().toUpperCase());
      toast.success("Coupon applied");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const createOrder = async () => {
    setBusy(true);
    try {
      const r = await api.post("/orders", { items, customer: form, payment_method: method, coupon });
      localStorage.setItem("kt_addr", JSON.stringify(form));
      setOrder(r.data);
      setTimeout(() => document.querySelector('[data-testid="upi-payment-panel"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const place = (e) => { e.preventDefault(); createOrder(); };
  const regenerate = () => { setOrder(null); createOrder(); };
  const paid = (o) => { clearCart(); nav(`/order/${o.order_number}`); };

  if (!cart.length) return (
    <div className="kt-container py-24 text-center"><p className="font-serif-display text-4xl">Your bag is empty</p><Link to="/collections/all" className="btn-primary mt-6">Continue Shopping</Link></div>
  );

  return (
    <form data-testid="checkout-page" onSubmit={place} className="kt-container grid gap-10 py-10 lg:grid-cols-12 lg:py-14">
      <div className="space-y-8 lg:col-span-7">
        <div><p className="eyebrow">Step 1 of 1 · Guest checkout</p><h1 className="mt-2 font-serif-display text-5xl font-semibold">Checkout</h1></div>
        <section className="rounded-3xl border border-[#E7E2DB] bg-white p-6 sm:p-8">
          <h2 className="font-serif-display text-2xl font-semibold">Delivery details</h2>
          <fieldset disabled={!!order} className="mt-5 grid gap-3 disabled:opacity-70 sm:grid-cols-2">
            {FIELDS.map(([k, l, cls]) => (
              <input key={k} data-testid={`checkout-${k}-input`} required type={k === "email" ? "email" : "text"} placeholder={l} aria-label={l} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={`rounded-xl border border-[#E7E2DB] bg-[#FAF7F2] px-4 py-3 text-sm outline-none focus:border-[#B85D43] ${cls}`} />
            ))}
          </fieldset>
        </section>
        <section className="rounded-3xl border border-[#E7E2DB] bg-white p-6 sm:p-8">
          <h2 className="font-serif-display text-2xl font-semibold">Payment</h2>
          <div data-testid="cod-disabled-notice" role="alert" className="mt-5 flex gap-3 rounded-2xl border border-[#E8B04B] bg-[#FFF4DC] p-4 text-sm leading-relaxed text-[#6B4A00]">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-[#C47F00]" /><p>{COD_NOTICE}</p>
          </div>
          <div className="mt-4 grid gap-3">
            <label data-testid="payment-method-upi" className="flex items-center gap-4 rounded-2xl border border-[#701A24] bg-[#F4E4DE] p-4">
              <input type="radio" name="pm" checked readOnly className="accent-[#701A24]" />
              <Smartphone className="h-5 w-5 text-[#701A24]" />
              <span><span className="block text-sm font-semibold">UPI QR – Prepaid</span><span className="text-xs text-[#78716C]">Google Pay, PhonePe, Paytm, BHIM · Extra 10% off</span></span>
            </label>
            <div data-testid="payment-method-cod" aria-disabled="true" className="flex cursor-not-allowed items-center gap-4 rounded-2xl border border-dashed border-[#D6CFC6] bg-[#F4EFEA] p-4 text-[#A8A29E]">
              <input type="radio" name="pm" disabled className="accent-[#701A24]" />
              <Banknote className="h-5 w-5" />
              <span><span className="block text-sm font-semibold line-through">Cash on Delivery</span><span className="text-xs">Temporarily disabled</span></span>
            </div>
          </div>
          {order && <UpiPayment order={order} onPaid={paid} onRegenerate={regenerate} regenerating={busy} />}
        </section>
      </div>
      <aside className="lg:col-span-5">
        <div className="sticky top-24 rounded-3xl border border-[#E7E2DB] bg-white p-6 sm:p-8">
          <h2 className="font-serif-display text-2xl font-semibold">Order summary</h2>
          <div className="mt-5 max-h-64 space-y-4 overflow-y-auto">
            {cart.map((i) => (
              <div key={i.key} className="flex gap-3">
                <img src={i.image} alt={i.name} className="h-20 w-16 rounded-lg object-cover" />
                <div className="flex-1 text-sm"><p className="line-clamp-2 font-medium">{i.name}</p><p className="text-xs text-[#78716C]">{i.size} · {i.color} · Qty {i.qty}</p></div>
                <span className="text-sm font-semibold">{inr(i.price * i.qty)}</span>
              </div>
            ))}
          </div>
          <div className="mt-5 flex gap-2">
            {coupon ? (
              <div data-testid="applied-coupon" className="flex flex-1 items-center justify-between rounded-full bg-[#1B4332] px-4 py-2.5 text-sm text-white"><span className="flex items-center gap-2"><Tag className="h-4 w-4" /> {coupon} applied</span><button type="button" data-testid="remove-coupon-button" disabled={!!order} onClick={() => setCoupon(null)}><X className="h-4 w-4" /></button></div>
            ) : (
              <>
                <input data-testid="coupon-input" value={couponInput} onChange={(e) => setCouponInput(e.target.value)} placeholder="Coupon code (e.g. KURTAANI10)" className="flex-1 rounded-full border border-[#E7E2DB] px-4 py-2.5 text-sm uppercase outline-none focus:border-[#B85D43]" />
                <button type="button" data-testid="apply-coupon-button" disabled={!!order} onClick={applyCoupon} className="rounded-full bg-[#1C1917] px-5 text-sm font-semibold text-white">Apply</button>
              </>
            )}
          </div>
          {quote && (
            <div className="mt-6 space-y-2.5 border-t border-[#E7E2DB] pt-5">
              <Row l="Subtotal" v={inr(quote.subtotal)} testid="summary-subtotal" />
              {quote.combo_discount > 0 && <Row green l={`2 for ₹329 combo × ${quote.combo_pairs}`} v={`− ${inr(quote.combo_discount)}`} testid="summary-combo" />}
              {quote.coupon_discount > 0 && <Row green l={`Coupon (${quote.coupon})`} v={`− ${inr(quote.coupon_discount)}`} testid="summary-coupon" />}
              {quote.bundle_discount > 0 && <Row green l="Buy 2 & Save" v={`− ${inr(quote.bundle_discount)}`} testid="summary-bundle" />}
              {quote.prepaid_discount > 0 && <Row green l={quote.combo_pairs ? "Prepaid extra 10% (non-combo items)" : "Prepaid extra 10%"} v={`− ${inr(quote.prepaid_discount)}`} testid="summary-prepaid" />}
              <Row l="Shipping" v="FREE" />
              <div className="flex justify-between border-t border-[#E7E2DB] pt-3 text-lg font-semibold"><span>Total</span><span data-testid="summary-total">{inr(quote.total)}</span></div>
            </div>
          )}
          {!order && <button data-testid="place-order-button" disabled={busy || !quote} className="btn-primary mt-6 w-full"><Lock className="h-4 w-4" /> {busy ? "Generating QR…" : `Proceed to Pay ${quote ? inr(quote.total) : ""}`}</button>}
          {order && <p data-testid="awaiting-payment-note" className="mt-6 rounded-2xl bg-[#F4E4DE] p-3 text-center text-xs text-[#701A24]">Order {order.order_number} is reserved – scan the QR in the Payment section to complete.</p>}
          <p className="mt-3 text-center text-xs text-[#78716C]">Free 7-day size exchange · Secure checkout</p>
        </div>
      </aside>
    </form>
  );
}
